import { useState } from "react";
import { normaliseRect, type CellPos, type CellRect } from "@heraion/shared";

// The wide grid's selection (#336 slice 6 part 2b, brief §7.3). One corner
// is the open cell (the exercise on the right, in that round), so the grid
// and the pane never disagree; the other is kept here. A cell opened from
// elsewhere (the pane, a problem) starts a new selection there. The field
// typed into is the open cell's. Platform free: the keys and the mouse are
// useGridKeys.web.ts.

const same = (a: CellPos | null, b: CellPos | null) => !!a && !!b && a.row === b.row && a.col === b.col;

export function useGridSelection({
  rows,
  cols,
  open,
  onOpen,
}: {
  rows: number;
  cols: number;
  /** The open cell, or null. */
  open: CellPos | null;
  onOpen: (p: CellPos) => void;
}) {
  const [far, setFar] = useState<{ at: CellPos; from: CellPos } | null>(null);
  // Selection mode (a finger has no Shift): the block runs from `anchor`, a
  // cell of its own that opens nothing on the right, to the far corner.
  const [selecting, setSelecting] = useState(false);
  const [anchor, setAnchor] = useState<CellPos | null>(null);
  const [editing, setEditing] = useState<{ at: CellPos; seed?: string } | null>(null);
  const inGrid = (p: CellPos) => p.row >= 0 && p.row < rows && p.col >= 0 && p.col < cols;
  // The block's fixed corner: the anchor in the mode, else the open cell.
  const base = selecting ? anchor : open;
  // Valid while the base is the one it was set from.
  const corner = far && same(far.from, base) && inGrid(far.at) ? far.at : base;
  const rect: CellRect | null = base && corner ? normaliseRect(base, corner) : null;
  const field = editing && same(editing.at, open) ? editing : null;
  const clamp = (p: CellPos): CellPos => ({
    row: Math.max(0, Math.min(rows - 1, p.row)),
    col: Math.max(0, Math.min(cols - 1, p.col)),
  });

  const select = (p: CellPos) => {
    setFar(null);
    setEditing(null);
    if (selecting) setAnchor(p);
    else if (!same(p, open)) onOpen(p);
  };
  // From `from`, which becomes the open cell (the anchor, in the mode) if it is not.
  const dragFrom = (from: CellPos, to: CellPos) => {
    if (selecting) setAnchor(from);
    else if (!same(from, open)) onOpen(from);
    setEditing(null);
    setFar({ at: clamp(to), from });
  };
  const extendTo = (p: CellPos) => (base ? dragFrom(base, p) : select(p));

  // Done, or Esc: out of the mode, and the block back to its open cell.
  const stopSelecting = () => {
    setSelecting(false);
    setAnchor(null);
    setFar(null);
  };

  return {
    rect,
    selecting,
    anchored: anchor !== null,
    /** Enters selection mode; with a cell, that cell is the anchor. Opens
     *  nothing on the right. */
    startSelecting: (p?: CellPos) => {
      setSelecting(true);
      setAnchor(p ?? null);
      setFar(null);
      setEditing(null);
    },
    stopSelecting,
    /** A tap in selection mode: the first sets the anchor, the later ones grow
     *  the block. Nothing opens on the right. */
    tap: (p: CellPos) => (anchor ? extendTo(p) : select(p)),
    /** The far corner: Shift with an arrow moves it. */
    corner,
    field,
    single: !!rect && rect.top === rect.bottom && rect.left === rect.right,
    select,
    extendTo,
    dragFrom,
    move: (dRow: number, dCol: number, extend: boolean) => {
      const from = (extend ? corner : base) ?? { row: 0, col: 0 };
      const to = clamp({ row: from.row + dRow, col: from.col + dCol });
      if (extend) extendTo(to);
      else select(to);
    },
    edit: (seed?: string) => {
      if (open) setEditing({ at: open, seed });
    },
    stopEdit: () => setEditing(null),
    collapse: () => setFar(null),
  };
}
